/**
 * Estimates — list page.
 *
 * Shows every estimate the current user is allowed to see (workers: their
 * drafts only; admin/supervisor: all). Includes status filter chips, a "+ New
 * estimate" CTA, and quick actions (download PDF, copy public link, delete).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Plus, FileText, Trash2, Download, Link2, Loader2, Send, Eye, FileSpreadsheet } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'sent', label: 'Sent' },
  { key: 'viewed', label: 'Viewed' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'converted', label: 'Converted' },
  { key: 'declined', label: 'Declined' },
  { key: 'expired', label: 'Expired' },
];

const STATUS_COLOURS = {
  draft: 'bg-slate-200 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  viewed: 'bg-indigo-100 text-indigo-700',
  accepted: 'bg-emerald-100 text-emerald-800',
  converted: 'bg-emerald-200 text-emerald-900',
  declined: 'bg-red-100 text-red-700',
  expired: 'bg-amber-100 text-amber-800',
};

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function Estimates() {
  const navigate = useNavigate();
  const { token, user, isAdmin, isSupervisor } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const canSend = isAdmin || isSupervisor;

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/estimates${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data.items || []);
    } catch (e) {
      toast({ title: 'Failed to load estimates', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-line */ }, [filter]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this estimate? Sent estimates will be removed from the customer-facing link too.')) return;
    try {
      await axios.delete(`${API}/estimates/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Estimate deleted' });
      fetchItems();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const handleDownload = async (est, kind = 'pdf') => {
    try {
      const res = await axios.get(`${API}/estimates/${est.id}/${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${est.estimate_number}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const copyPublicLink = (est) => {
    const url = `${window.location.origin}/estimate/${est.public_token}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Public link copied', description: url });
  };

  const recipientName = (e) => e.customer_id ? '(customer record)' : (e.prospect?.name || '—');

  return (
    <div className="p-6 space-y-6" data-testid="estimates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Estimates</h1>
          <p className="text-sm text-slate-400 mt-1">
            Build a quote in the field, email it as a branded PDF, customer accepts online → fixed-bid Job created automatically.
          </p>
        </div>
        <Button onClick={() => navigate('/estimates/new')} data-testid="new-estimate-btn">
          <Plus className="w-4 h-4 mr-2" /> New estimate
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="estimate-filter-chips">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filter === f.key ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-300 mb-2">No estimates yet</p>
            <p className="text-sm text-slate-500 mb-6">Build your first estimate and email it to a customer in under 2 minutes.</p>
            <Button onClick={() => navigate('/estimates/new')} data-testid="empty-new-estimate-btn">
              <Plus className="w-4 h-4 mr-2" /> Create estimate
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-800 text-slate-300">
                  <tr>
                    <th className="text-left px-4 py-3">Estimate</th>
                    <th className="text-left px-4 py-3">Recipient</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Total</th>
                    <th className="text-left px-4 py-3">Valid until</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((est) => (
                    <tr key={est.id} className="border-t border-slate-800 hover:bg-slate-800/50" data-testid={`estimate-row-${est.estimate_number}`}>
                      <td className="px-4 py-3">
                        <button
                          className="font-mono font-semibold text-blue-400 hover:underline"
                          onClick={() => navigate(`/estimates/${est.id}`)}
                          data-testid={`estimate-link-${est.estimate_number}`}
                        >
                          {est.estimate_number}
                        </button>
                        <div className="text-xs text-slate-500 truncate max-w-xs">{est.title}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{recipientName(est)}</td>
                      <td className="px-4 py-3">
                        <Badge className={`${STATUS_COLOURS[est.status] || 'bg-slate-300'} border-0 capitalize`}>{est.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-200">{fmtMoney(est?.totals?.grand_total)}</td>
                      <td className="px-4 py-3 text-slate-400">{fmtDate(est.valid_until)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" title="View" onClick={() => navigate(`/estimates/${est.id}`)} data-testid={`view-${est.estimate_number}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" title="Download line items (XLSX)" onClick={() => handleDownload(est, 'xlsx')} data-testid={`download-xlsx-${est.estimate_number}`}><FileSpreadsheet className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" title="Download PDF" onClick={() => handleDownload(est)} data-testid={`download-${est.estimate_number}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                          {est.status !== 'draft' && (
                            <Button size="icon" variant="ghost" title="Copy public link" onClick={() => copyPublicLink(est)} data-testid={`copy-link-${est.estimate_number}`}>
                              <Link2 className="w-4 h-4" />
                            </Button>
                          )}
                          {!['accepted', 'converted'].includes(est.status) && (
                            <Button size="icon" variant="ghost" title="Delete" onClick={() => handleDelete(est.id)} data-testid={`delete-${est.estimate_number}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
